import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getAuthToken } from './tokenStore';
import { hasRefresher, refreshAccessToken } from './authRefresh';
import { orchestrationUrl } from '../config/environments';
import type { ProblemDetail } from '../interfaces/chat';

/**
 * Shared axios instance for orchestration JSON requests. Per API_STANDARDS the
 * Bearer token is injected by a request interceptor so call sites never
 * assemble auth headers. The tenant is resolved server-side from the
 * DELEGATED token itself (token-exchange grant) — sending X-Tenant-Id is
 * rejected with 403 for this token type. SSE streaming uses the fetch API
 * directly (axios can't expose a readable byte stream in the browser) — see
 * {@link getSseHeaders}.
 */
export const archerClient = axios.create();

archerClient.interceptors.request.use((config) => {
  config.headers.set('Authorization', `Bearer ${getAuthToken()}`);
  config.headers.set('Content-Type', 'application/json');
  config.headers.set('Accept', 'application/json, application/problem+json');
  return config;
});

// On a 401 (expired/invalid token), refresh once and retry the request
// transparently. The request interceptor re-reads the refreshed token from the
// store, so the retry carries valid auth. SSE (fetch-based) isn't covered here —
// the proactive scheduler keeps its token fresh and it reconnects on next send.
archerClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (config && !config._retried && error.response?.status === 401 && hasRefresher()) {
      config._retried = true;
      const token = await refreshAccessToken();
      if (token) return archerClient(config);
    }
    return Promise.reject(error);
  },
);

/** Headers for the SSE GET stream (fetch-based). */
export function getSseHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${getAuthToken()}`,
    Accept: 'text/event-stream',
  };
}

export { orchestrationUrl };

function isProblemDetail(value: unknown): value is ProblemDetail {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('title' in value || 'detail' in value || 'status' in value || 'code' in value)
  );
}

/**
 * Extracts a user-facing message from an unknown error, preferring RFC 7807
 * problem-detail fields returned by the orchestration API.
 */
export function extractErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (isProblemDetail(data)) {
      return data.detail || data.title || error.message || fallback;
    }
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}
